import { supabase } from '../supabase';

export type PropuestaFavoritaResumen = {
  id: string;
  propuestaId: string;
  titulo: string;
  restauranteNombre: string | null;
  edicionNombre: string | null;
  createdAt: string;
};

/**
 * Lista las propuestas favoritas de un usuario (propio o, con permiso, desde dashboard).
 */
export async function listPropuestasFavoritasPorUsuario(
  userId: string
): Promise<PropuestaFavoritaResumen[]> {
  const uid = userId?.trim();
  if (!uid) return [];

  const { data, error } = await supabase
    .from('propuestas_favoritas')
    .select(
      `
      id,
      created_at,
      propuesta_id,
      propuestas (
        id,
        titulo,
        restaurantes ( nombre_mostrar, nombre_oficial ),
        ediciones ( nombre )
      )
    `
    )
    .eq('user_id', uid)
    .order('created_at', { ascending: false });

  if (error) throw error;

  const out: PropuestaFavoritaResumen[] = [];
  for (const row of data ?? []) {
    const prop = (row as { propuestas?: unknown }).propuestas as
      | {
          id?: string;
          titulo?: string;
          restaurantes?: { nombre_mostrar?: string; nombre_oficial?: string } | null;
          ediciones?: { nombre?: string } | null;
        }
      | null;
    if (!prop?.id) continue;
    const rest = prop.restaurantes;
    out.push({
      id: String((row as { id: string }).id),
      propuestaId: String(prop.id),
      titulo: String(prop.titulo ?? 'Sin título').trim() || 'Sin título',
      restauranteNombre:
        rest?.nombre_mostrar?.trim() || rest?.nombre_oficial?.trim() || null,
      edicionNombre: prop.ediciones?.nombre?.trim() || null,
      createdAt: String((row as { created_at?: string }).created_at ?? ''),
    });
  }
  return out;
}

export async function listPropuestaFavoritaIdsPorUsuario(userId: string): Promise<Set<string>> {
  const uid = userId?.trim();
  if (!uid) return new Set();
  const { data, error } = await supabase
    .from('propuestas_favoritas')
    .select('propuesta_id')
    .eq('user_id', uid);
  if (error) throw error;
  return new Set(
    (data ?? [])
      .map((r) => String((r as { propuesta_id?: string }).propuesta_id ?? '').trim())
      .filter(Boolean)
  );
}

export async function countPropuestasFavoritasPorUsuario(userId: string): Promise<number> {
  const uid = userId?.trim();
  if (!uid) return 0;
  const { count, error } = await supabase
    .from('propuestas_favoritas')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', uid);
  if (error) throw error;
  return count ?? 0;
}

/** Añade o quita favorito. Devuelve true si queda como favorita. */
export async function togglePropuestaFavorita(
  userId: string,
  propuestaId: string
): Promise<boolean> {
  const uid = userId?.trim();
  const pid = propuestaId?.trim();
  if (!uid || !pid) throw new Error('Usuario o propuesta inválidos');

  const { data: existing, error: findErr } = await supabase
    .from('propuestas_favoritas')
    .select('id')
    .eq('user_id', uid)
    .eq('propuesta_id', pid)
    .maybeSingle();
  if (findErr) throw findErr;

  if (existing?.id) {
    const { error } = await supabase.from('propuestas_favoritas').delete().eq('id', existing.id);
    if (error) throw error;
    return false;
  }

  const { error } = await supabase.from('propuestas_favoritas').insert({
    user_id: uid,
    propuesta_id: pid,
  });
  if (error) throw error;
  return true;
}
